import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function InventoryAdmin() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/products").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const adjust = async (id, delta) => { await api.post(`/admin/inventory/${id}/adjust?delta=${delta}&reason=manual`); toast.success("Adjusted"); load(); };
  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Inventory</h1>
      <div className="admin-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[#8C867D] text-xs uppercase tracking-widest text-left"><tr><th className="p-3">SKU</th><th>Name</th><th>Stock</th><th>Threshold</th><th></th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className={`border-t border-[#27272A] ${p.stock <= p.low_stock_threshold ? "bg-[#3a1e1e]/30" : ""}`}>
                <td className="p-3 font-mono text-xs">{p.sku}</td>
                <td>{p.name}</td>
                <td className={p.stock <= p.low_stock_threshold ? "text-[#C5A059]" : ""}>{p.stock}</td>
                <td className="text-xs">{p.low_stock_threshold}</td>
                <td className="space-x-2">
                  <button onClick={() => adjust(p.id, 1)} className="px-2 py-1 text-xs border border-[#27272A]" data-testid={`inv-plus-${p.sku}`}>+1</button>
                  <button onClick={() => adjust(p.id, 10)} className="px-2 py-1 text-xs border border-[#27272A]">+10</button>
                  <button onClick={() => adjust(p.id, -1)} className="px-2 py-1 text-xs border border-[#27272A]" data-testid={`inv-minus-${p.sku}`}>−1</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CustomersAdmin() {
  const [c, setC] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setC(r.data)); }, [api]);
  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Customers</h1>
      <div className="admin-surface"><table className="w-full text-sm">
        <thead className="text-[#8C867D] text-xs uppercase tracking-widest text-left"><tr><th className="p-3">Name</th><th>Email</th><th>Phone</th><th>Joined</th></tr></thead>
        <tbody>{c.map(u => <tr key={u.id} className="border-t border-[#27272A]"><td className="p-3">{u.name}</td><td className="text-xs">{u.email}</td><td className="text-xs">{u.phone||"—"}</td><td className="text-xs">{new Date(u.created_at).toLocaleDateString()}</td></tr>)}</tbody>
      </table></div>
    </div>
  );
}

export function AuditLogs() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, [api]);
  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Audit Log</h1>
      <div className="admin-surface"><table className="w-full text-sm">
        <thead className="text-[#8C867D] text-xs uppercase tracking-widest text-left"><tr><th className="p-3">Time</th><th>User</th><th>Role</th><th>Action</th><th>Target</th><th>Old</th><th>New</th></tr></thead>
        <tbody>{logs.map(l => (
          <tr key={l.id} className="border-t border-[#27272A] text-xs">
            <td className="p-3 font-mono">{new Date(l.timestamp).toLocaleString()}</td>
            <td>{l.user_email}</td><td className="uppercase">{l.user_role}</td><td className="text-[#C5A059]">{l.action}</td><td className="font-mono">{l.target||"—"}</td>
            <td>{l.old_value ? JSON.stringify(l.old_value).slice(0,40) : "—"}</td>
            <td>{l.new_value ? JSON.stringify(l.new_value).slice(0,40) : "—"}</td>
          </tr>))}</tbody>
      </table></div>
    </div>
  );
}

export function Coupons() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ code: "", type: "percent", value: 10, min_order: 0, enabled: true });
  const load = useCallback(() => api.get("/admin/coupons").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => { try { await api.post("/admin/coupons", f); toast.success("Created"); load(); } catch(e) { toast.error("Error"); } };
  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Coupons</h1>
      <div className="admin-surface p-6 mb-6 grid grid-cols-5 gap-3">
        <input placeholder="Code" value={f.code} onChange={(e) => setF({...f, code: e.target.value})} className="admin-input" data-testid="coupon-code" />
        <select value={f.type} onChange={(e) => setF({...f, type: e.target.value})} className="admin-input"><option value="percent">Percent</option><option value="fixed">Fixed</option></select>
        <input type="number" placeholder="Value" value={f.value} onChange={(e) => setF({...f, value: +e.target.value})} className="admin-input" />
        <input type="number" placeholder="Min Order" value={f.min_order} onChange={(e) => setF({...f, min_order: +e.target.value})} className="admin-input" />
        <button onClick={save} className="bg-[#C5A059] text-[#121212] text-xs uppercase tracking-widest" data-testid="coupon-save">Create</button>
      </div>
      <div className="admin-surface"><table className="w-full text-sm">
        <thead className="text-[#8C867D] text-xs uppercase tracking-widest text-left"><tr><th className="p-3">Code</th><th>Type</th><th>Value</th><th>Used</th></tr></thead>
        <tbody>{items.map(c => <tr key={c.id} className="border-t border-[#27272A]"><td className="p-3 font-mono">{c.code}</td><td className="text-xs">{c.type}</td><td>{c.value}{c.type==='percent'?'%':''}</td><td className="text-xs">{c.used_count||0}</td></tr>)}</tbody>
      </table></div>
    </div>
  );
}
